/**
 * CLTHNG — parallel analytics (clthng_* events, attribution, cart helpers).
 * Extends window.CLTHAnalytics.trackEvent; does not replace it.
 */
(function () {
  'use strict';

  var ATTRIBUTION_KEY = 'clthng_session_attribution';
  var LAST_CART_KEY = 'clthng_last_cart_snapshot';
  var BEGIN_CHECKOUT_KEY = 'clthng_begin_checkout_fired';
  var PURCHASE_KEY = 'clthng_purchase_fired';

  var beginCheckoutFired = false;
  var abandonSent = false;
  var lastCartBundle = null;
  var originalFetch = window.fetch;

  // ---------- 1. Session attribution ----------
  function captureSessionAttribution() {
    try {
      if (sessionStorage.getItem(ATTRIBUTION_KEY)) return;
    } catch (e) {
      return;
    }

    var params = new URLSearchParams(window.location.search);
    var referrer = document.referrer || '';
    var referrerDomain = '';
    try {
      if (referrer) referrerDomain = new URL(referrer).hostname;
    } catch (err) {}

    var fbclid = params.get('fbclid');
    var gclid = params.get('gclid');
    var ttclid = params.get('ttclid');

    var trafficSource = 'direct';
    if (fbclid) trafficSource = 'meta_paid';
    else if (gclid) trafficSource = 'google_paid';
    else if (ttclid) trafficSource = 'tiktok_paid';
    else if (referrerDomain) {
      if (/google\.|bing\.|duckduckgo\.|yahoo\./.test(referrerDomain)) {
        trafficSource = 'organic_search';
      } else if (/instagram\.|facebook\.|tiktok\.|twitter\.|x\.com|youtube\./.test(referrerDomain)) {
        trafficSource = 'social_organic';
      } else {
        trafficSource = 'referral';
      }
    }

    var attribution = {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
      utm_term: params.get('utm_term') || '',
      fbclid: fbclid || '',
      gclid: gclid || '',
      ttclid: ttclid || '',
      landing_page_path: window.location.pathname,
      referrer_domain: referrerDomain,
      traffic_source_inferred: trafficSource,
      session_started_at: Date.now()
    };

    try {
      sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    } catch (e2) {}
  }

  function getAttribution() {
    try {
      return JSON.parse(sessionStorage.getItem(ATTRIBUTION_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  // ---------- 2. Cart snapshot + helper ----------
  function persistCartSnapshot(bundle) {
    lastCartBundle = bundle;
    try {
      sessionStorage.setItem(LAST_CART_KEY, JSON.stringify(bundle));
    } catch (e) {}
  }

  function loadCartSnapshotFromStorage() {
    try {
      var raw = sessionStorage.getItem(LAST_CART_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  async function getCartItems() {
    try {
      var res = await originalFetch.call(window, '/cart.js', { credentials: 'same-origin' });
      if (!res.ok) {
        var empty = { items: [], value: 0, currency: 'USD', item_count: 0 };
        persistCartSnapshot(empty);
        return empty;
      }
      var cart = await res.json();
      var bundle = {
        currency: cart.currency || 'USD',
        value: (cart.total_price || 0) / 100,
        item_count: cart.item_count || 0,
        items: (cart.items || []).map(function (line) {
          return {
            item_id: line.sku || String(line.variant_id),
            item_name: line.product_title,
            item_variant: line.variant_title || '',
            item_brand: 'CLTHNG',
            item_category: line.product_type || '',
            price: (line.price || 0) / 100,
            quantity: line.quantity
          };
        })
      };
      persistCartSnapshot(bundle);
      return bundle;
    } catch (e) {
      var fallback = { items: [], value: 0, currency: 'USD', item_count: 0 };
      persistCartSnapshot(fallback);
      return fallback;
    }
  }

  // Avoid recursive fetch when getCartItems runs inside fetch hook
  function getCartItemsInternal() {
    return getCartItems();
  }

  // ---------- Direct GA4 Measurement Protocol sender ----------
  // Bypasses gtag for events that need:
  //   - Guaranteed items array serialization (gtag drops items[] for custom events)
  //   - Beacon transport for unload events (gtag's transport_type: 'beacon' is unreliable)
  // Used by: clthng_add_to_cart, clthng_cart_view, clthng_remove_from_cart,
  //          clthng_cart_abandoned, clthng_checkout_step_drop
  function sendGA4Event(eventName, eventParams, useBeacon) {
    var GA4_ID = 'G-1EG5V48C71';
    var collectUrl = 'https://www.google-analytics.com/g/collect';

    var clientId = '';
    var cookieRows = [];
    try {
      cookieRows = document.cookie.split('; ');
      for (var c = 0; c < cookieRows.length; c++) {
        if (cookieRows[c].indexOf('_ga=') === 0) {
          var gaCookie = cookieRows[c].split('=')[1];
          var parts = gaCookie.split('.');
          if (parts.length >= 4) clientId = parts[2] + '.' + parts[3];
          break;
        }
      }
    } catch (e) {}
    if (!clientId) clientId = String(Date.now()) + '.' + String(Math.floor(Math.random() * 1e10));

    var sessionId = '';
    try {
      var gaIdSuffix = GA4_ID.replace('G-', '');
      var prefix = '_ga_' + gaIdSuffix + '=';
      for (var s = 0; s < cookieRows.length; s++) {
        if (cookieRows[s].indexOf(prefix) === 0) {
          var sc = cookieRows[s].split('=')[1];
          var sparts = sc.split('.');
          if (sparts.length >= 3) sessionId = sparts[2];
          break;
        }
      }
    } catch (e2) {}

    var pageCtx = {};
    if (window.CLTHAnalytics && typeof window.CLTHAnalytics.getPageContext === 'function') {
      pageCtx = window.CLTHAnalytics.getPageContext() || {};
    }

    var attribution = getAttribution();
    var mergedParams = Object.assign({}, pageCtx, eventParams, {
      traffic_source_inferred: attribution.traffic_source_inferred,
      utm_source: attribution.utm_source,
      utm_campaign: attribution.utm_campaign,
      landing_page_path: attribution.landing_page_path
    });

    var qp = [];
    qp.push('v=2');
    qp.push('tid=' + encodeURIComponent(GA4_ID));
    qp.push('cid=' + encodeURIComponent(clientId));
    qp.push('en=' + encodeURIComponent(eventName));
    qp.push('dl=' + encodeURIComponent(window.location.href));
    qp.push('dt=' + encodeURIComponent(document.title || ''));
    if (sessionId) qp.push('sid=' + encodeURIComponent(sessionId));
    if (mergedParams.currency) {
      qp.push('cu=' + encodeURIComponent(String(mergedParams.currency)));
    }

    var bodyParts = [];
    var key;
    for (key in mergedParams) {
      if (!Object.prototype.hasOwnProperty.call(mergedParams, key)) continue;
      var val = mergedParams[key];
      if (val === null || val === undefined || val === '') continue;
      if (key === 'items') continue;
      if (key === 'currency') continue;
      if (typeof val === 'number' && !isNaN(val)) {
        bodyParts.push('epn.' + key + '=' + val);
      } else if (typeof val === 'boolean') {
        bodyParts.push('ep.' + key + '=' + (val ? 'true' : 'false'));
      } else {
        bodyParts.push('ep.' + key + '=' + encodeURIComponent(String(val)));
      }
    }

    if (Array.isArray(mergedParams.items)) {
      mergedParams.items.forEach(function (item, i) {
        if (!item) return;
        var prIdx = i + 1;
        var itemFields = [];
        if (item.item_id != null && item.item_id !== '') {
          itemFields.push('id' + encodeURIComponent(String(item.item_id)));
        }
        if (item.item_name) itemFields.push('nm' + encodeURIComponent(String(item.item_name)));
        if (item.item_variant) itemFields.push('va' + encodeURIComponent(String(item.item_variant)));
        if (item.item_brand) itemFields.push('br' + encodeURIComponent(String(item.item_brand)));
        if (item.item_category) itemFields.push('ca' + encodeURIComponent(String(item.item_category)));
        if (item.price !== undefined && item.price !== null && !isNaN(item.price)) {
          itemFields.push('pr' + Number(item.price));
        }
        if (item.quantity !== undefined && item.quantity !== null && !isNaN(item.quantity)) {
          itemFields.push('qt' + Number(item.quantity));
        }
        if (itemFields.length) bodyParts.push('pr' + prIdx + '=' + itemFields.join('~'));
      });
    }

    var fullPayload = qp.join('&') + '&' + bodyParts.join('&');

    if (useBeacon && typeof navigator.sendBeacon === 'function') {
      try {
        var blob = new Blob([fullPayload], { type: 'application/x-www-form-urlencoded' });
        if (navigator.sendBeacon(collectUrl, blob)) return true;
      } catch (be) {
        /* fall through to fetch */
      }
    }

    try {
      originalFetch.call(window, collectUrl, {
        method: 'POST',
        body: fullPayload,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        keepalive: true,
        credentials: 'omit'
      }).catch(function () {});
      return true;
    } catch (e3) {
      return false;
    }
  }

  // ---------- 3. Extend CLTHAnalytics.trackEvent ----------
  function extendTrackEvent() {
    if (!window.CLTHAnalytics || typeof window.CLTHAnalytics.trackEvent !== 'function') {
      return setTimeout(extendTrackEvent, 50);
    }
    if (window.CLTHAnalytics.__clthngExtended) return;

    var original = window.CLTHAnalytics.trackEvent.bind(window.CLTHAnalytics);
    window.CLTHAnalytics.trackEvent = function (eventName, params) {
      var p = params || {};
      var attribution = getAttribution();
      var enriched = Object.assign({}, p, {
        traffic_source_inferred: attribution.traffic_source_inferred,
        utm_source: attribution.utm_source,
        utm_campaign: attribution.utm_campaign,
        landing_page_path: attribution.landing_page_path
      });
      return original(eventName, enriched);
    };
    window.CLTHAnalytics.__clthngExtended = true;
    window.CLTHAnalytics.getCartItems = getCartItemsInternal;
    window.CLTHAnalytics.getAttribution = getAttribution;
  }

  // ---------- Patch window.gtag to inject attribution on every event ----------
  // More reliable than patching CLTHAnalytics.trackEvent because theme.liquid
  // captures a local reference to trackEvent at load time which bypasses our
  // wrapper. gtag is the common chokepoint for every event in the page.
  function extendGtag() {
    if (typeof window.gtag !== 'function') {
      return setTimeout(extendGtag, 50);
    }
    if (window.gtag.__clthngPatched) return;

    var originalGtag = window.gtag;
    window.gtag = function () {
      if (arguments.length >= 3 && arguments[0] === 'event') {
        var eventName = arguments[1];
        var params = arguments[2];

        if (typeof eventName === 'string' && typeof params === 'object' && params !== null) {
          var attribution = getAttribution();
          if (!params.traffic_source_inferred && attribution.traffic_source_inferred) {
            params.traffic_source_inferred = attribution.traffic_source_inferred;
          }
          if (!params.utm_source && attribution.utm_source) {
            params.utm_source = attribution.utm_source;
          }
          if (!params.utm_campaign && attribution.utm_campaign) {
            params.utm_campaign = attribution.utm_campaign;
          }
          if (!params.landing_page_path && attribution.landing_page_path) {
            params.landing_page_path = attribution.landing_page_path;
          }
        }
      }
      return originalGtag.apply(this, arguments);
    };
    window.gtag.__clthngPatched = true;
  }

  // ---------- 4. Checkout / purchase flags ----------
  function markCheckoutStarted() {
    beginCheckoutFired = true;
    try {
      sessionStorage.setItem(BEGIN_CHECKOUT_KEY, '1');
    } catch (e) {}
  }

  function readCheckoutStarted() {
    if (beginCheckoutFired) return true;
    try {
      return sessionStorage.getItem(BEGIN_CHECKOUT_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function readPurchaseFired() {
    try {
      return sessionStorage.getItem(PURCHASE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  // ---------- 5. Parallel cart events (Measurement Protocol — gtag drops items[] on custom names) ----------
  async function fireCartView(triggerSource) {
    var cart = await getCartItemsInternal();
    if (!cart.items || cart.items.length === 0) return;
    sendGA4Event(
      'clthng_cart_view',
      {
        currency: cart.currency,
        value: cart.value,
        item_count: cart.item_count,
        items: cart.items,
        trigger_source: triggerSource || 'unknown'
      },
      false
    );
  }

  async function fireAddToCartParallel(triggerSource) {
    var cart = await getCartItemsInternal();
    sendGA4Event(
      'clthng_add_to_cart',
      {
        currency: cart.currency,
        value: cart.value,
        item_count: cart.item_count,
        items: cart.items,
        trigger_source: triggerSource || 'unknown'
      },
      false
    );
  }

  async function fireRemoveFromCart(hint) {
    var cart = await getCartItemsInternal();
    sendGA4Event(
      'clthng_remove_from_cart',
      {
        currency: cart.currency,
        value: cart.value,
        remaining_item_count: cart.item_count,
        items: cart.items,
        removal_trigger: hint || 'unknown'
      },
      false
    );
  }

  // ---------- 6. Abandonment (sync + beacon; snapshot-based) ----------
  function getSnapshotForUnload() {
    if (lastCartBundle && lastCartBundle.items) return lastCartBundle;
    var s = loadCartSnapshotFromStorage();
    if (s && s.items) return s;
    return { items: [], value: 0, currency: 'USD', item_count: 0 };
  }

  function flushAbandonmentEvents() {
    if (abandonSent) return;

    if (readPurchaseFired()) return;

    var path = window.location.pathname || '';
    var onCheckoutPage = /^\/checkouts\//.test(path);
    var checkoutStarted = readCheckoutStarted();
    var cart = getSnapshotForUnload();

    if (!checkoutStarted && !onCheckoutPage && cart.items && cart.items.length > 0) {
      sendGA4Event(
        'clthng_cart_abandoned',
        {
          currency: cart.currency,
          value: cart.value,
          item_count: cart.item_count,
          items: cart.items
        },
        true
      );
      abandonSent = true;
      return;
    }

    // NOTE: This branch cannot fire in production. Shopify's checkout pages
    // (/checkouts/*) do not load theme assets, so this extension never runs
    // there and the unload listeners below do not exist on checkout. Retained
    // as scaffolding for a future Customer Events / Web Pixel implementation,
    // which is the only supported way to instrument checkout step drops on
    // Shopify. See: https://shopify.dev/docs/api/web-pixels-api
    if (onCheckoutPage) {
      sendGA4Event(
        'clthng_checkout_step_drop',
        {
          checkout_url_path: path
        },
        true
      );
      abandonSent = true;
    }
  }

  // ---------- 7. dataLayer.push (drawer + checkout intent) ----------
  function installDataLayerHook() {
    window.dataLayer = window.dataLayer || [];
    var dl = window.dataLayer;
    if (dl.__clthngPatched) return;
    var originalPush = dl.push.bind(dl);
    dl.push = function () {
      var payload = arguments[0];
      var result = originalPush.apply(dl, arguments);
      try {
        if (payload && typeof payload === 'object' && payload.event === 'drawer_opened') {
          fireCartView('cart_drawer_open');
        }
        if (payload && typeof payload === 'object' && payload.event === 'checkout_clicked') {
          markCheckoutStarted();
        }
      } catch (e) {}
      return result;
    };
    dl.__clthngPatched = true;
  }

  // ---------- 8. fetch interception (cart mutations) ----------
  function installFetchHook() {
    if (window.__clthngFetchPatched) return;
    window.__clthngFetchPatched = true;

    window.fetch = function () {
      var args = arguments;
      var url = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url ? args[0].url : '';
      var isCartAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);
      var isCartChange = /\/cart\/change(\.js)?(\?|$)/.test(url);
      var isCartUpdate = /\/cart\/update(\.js)?(\?|$)/.test(url);

      return originalFetch.apply(this, args).then(function (response) {
        if (!response || !response.ok) return response;

        if (isCartAdd) {
          setTimeout(function () {
            fireAddToCartParallel('cart_add_api');
          }, 50);
          return response;
        }

        if (isCartChange || isCartUpdate) {
          var opts = args[1] || {};
          var body = opts.body;
          var isRemove = false;
          if (body != null) {
            try {
              var parsed = typeof body === 'string' ? JSON.parse(body) : body;
              if (parsed && (parsed.quantity === 0 || parsed.quantity === '0')) {
                isRemove = true;
              }
              if (parsed && parsed.line !== undefined && (parsed.quantity === 0 || parsed.quantity === '0')) {
                isRemove = true;
              }
            } catch (e) {
              if (typeof body === 'string' && /(?:^|&)quantity=(0|0\.0)(?:&|$)/.test(body)) {
                isRemove = true;
              }
            }
          }
          if (isRemove) {
            setTimeout(function () {
              fireRemoveFromCart('cart_change_api');
            }, 50);
          }
        }
        return response;
      });
    };
  }

  // ---------- 9. Thank-you URL (belt-and-suspenders with Liquid) ----------
  function markPurchaseIfThankYouPage() {
    try {
      if (/\/thank[_-]you/i.test(window.location.pathname)) {
        sessionStorage.setItem(PURCHASE_KEY, '1');
      }
    } catch (e) {}
  }

  // ---------- 10. Init ----------
  function init() {
    captureSessionAttribution();

    try {
      if (sessionStorage.getItem(BEGIN_CHECKOUT_KEY) === '1') beginCheckoutFired = true;
    } catch (e) {}

    extendTrackEvent();
    extendGtag();
    installDataLayerHook();
    installFetchHook();

    markPurchaseIfThankYouPage();

    // Prime cart snapshot for unload paths (non-blocking)
    getCartItemsInternal().catch(function () {});

    window.addEventListener('beforeunload', flushAbandonmentEvents);
    window.addEventListener('pagehide', flushAbandonmentEvents);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') flushAbandonmentEvents();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
